import { authHeaders } from '../../../core/api/authHeaders';

const PROXY_BASE = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');

/**
 * Channels and announcements (server/chat/channels.js).
 *
 * Every change goes through the proxy: channel membership and posting rights live in
 * channels/{id}, which the database rules let only the server write. Messages are still
 * sent the normal way; the rules enforce who may post.
 *
 * Every function throws an Error whose message is fit to show the user.
 */
async function call(method, path, body) {
    let res;
    try {
        res = await fetch(`${PROXY_BASE}${path}`, {
            method,
            headers: { ...(await authHeaders()), ...(body ? { 'Content-Type': 'application/json' } : {}) },
            body: body ? JSON.stringify(body) : undefined,
        });
    } catch {
        throw new Error('Channels are unavailable — check your connection.');
    }
    let data = null;
    try { data = await res.json(); } catch { /* non-JSON error page */ }
    if (!res.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
    return data;
}

const enc = encodeURIComponent;

export const ChannelService = {
    /** @returns {Promise<{mine: object[], discoverable: object[], canCreate: boolean, templates: object[]}>} */
    list: () => call('GET', '/api/channels'),
    create: (input) => call('POST', '/api/channels', input).then(r => r.channel),
    join: (id) => call('POST', `/api/channels/${enc(id)}/join`).then(r => r.channel),
    leave: (id) => call('POST', `/api/channels/${enc(id)}/leave`),
    changeMembers: (id, { add = [], remove = [] }) => call('POST', `/api/channels/${enc(id)}/members`, { add, remove }),
    setAdmin: (id, uid, admin) => call('POST', `/api/channels/${enc(id)}/admins`, { uid, admin }),
    update: (id, patch) => call('PATCH', `/api/channels/${enc(id)}`, patch),
    announce: (id, text, ackDeadline) => call('POST', `/api/channels/${enc(id)}/announce`, { text, ackDeadline: ackDeadline || undefined }),
    acknowledge: (id, messageId) => call('POST', `/api/channels/${enc(id)}/ack/${enc(messageId)}`),
    ackStatus: (id, messageId) => call('GET', `/api/channels/${enc(id)}/ack/${enc(messageId)}`),
    /** @returns {Promise<object[]>} my outstanding acknowledgements, soonest deadline first */
    pendingAcks: () => call('GET', '/api/channel-acks/pending').then(r => r.pending || []),
};

/** A chat-list row for a channel, in the shape ChatWindow expects. */
export const channelToChat = (c) => ({
    chatId: c.id,
    id: c.id,
    targetUserId: c.id,
    name: c.name,
    type: 'channel',
    isGroup: true,
    isChannel: true,
    kind: c.kind,
    photo: null,
    memberCount: c.memberCount,
});
